import type { RoomRailGroups, RoomRailSessionNode } from "./projectRoomRailGroups";

/**
 * Can 80 (the principal, 17 Oct 2026: "the quantity of sessions is
 * overwhelming"): which sessions fold away under Earlier. A session is quiet
 * when it and every session under it have been untouched for the chosen days,
 * none of them is pinned, working or waiting on the person, and none is the
 * one open. A session's time is the later of its last conversation on this
 * Desk and the World's own time for it (worldActivity: its latest execution
 * or retirement step in the World, else its birth, from any device, Pocket
 * included). A session with neither (the World's times not read yet, or an
 * older World) is judged by its latest possible birth: the World numbers
 * sessions in birth order, so one born before a session this Desk knows was
 * born no later than that session's first conversation here began
 * (birthBound). One with no time at all stays in view. View only: nothing is
 * archived or changed.
 */
export interface RoomRailQuietRule {
  /** Last activity per session, ms since the epoch (from its conversations). */
  readonly activity: ReadonlyMap<string, number>;
  /** When each session was last active in its World, from any device (ms). */
  readonly worldActivity?: ReadonlyMap<string, number> | undefined;
  /** Sessions active at or after this instant stay in view. */
  readonly cutoff: number;
  readonly pinned: ReadonlySet<string>;
  /** A session with a live status (working, needs you, a new result) stays. */
  readonly live: (sessionId: string) => boolean;
  /** The latest instant a session born at this ledger position can have been born. */
  readonly birthBound?: ((bornSeq: number) => number | undefined) | undefined;
}

/**
 * From the sessions this Desk knows ([bornSeq, first conversation began, ms]),
 * the latest possible birth of any session at a ledger position: the earliest
 * known start among sessions born at or after it. Undefined past the last one.
 */
export function birthBoundFromAnchors(
  anchors: ReadonlyArray<readonly [number, number]>,
): (bornSeq: number) => number | undefined {
  const sorted = [...anchors].sort((a, b) => a[0] - b[0]);
  const seqs = sorted.map(([seq]) => seq);
  const suffixMin: number[] = Array.from({ length: sorted.length }, () => 0);
  let min = Number.POSITIVE_INFINITY;
  for (let i = sorted.length - 1; i >= 0; i--) {
    min = Math.min(min, sorted[i]![1]);
    suffixMin[i] = min;
  }
  return (bornSeq) => {
    let lo = 0;
    let hi = seqs.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (seqs[mid]! < bornSeq) lo = mid + 1;
      else hi = mid;
    }
    return lo < seqs.length ? suffixMin[lo] : undefined;
  };
}

export const QUIET_DAY_MS = 86_400_000;

/**
 * One round of reading the World's session times: which sessions this round
 * has asked about. A round is one World and one refresh slot; within it each
 * session is asked once, however often the index changes (it arrives page by
 * page, and every World move changes it), and a session that appears later is
 * asked then. A new round asks about every session again.
 */
export interface SessionTimesRound {
  readonly key: string;
  readonly asked: ReadonlySet<string>;
}
export function sessionsToAsk(
  round: SessionTimesRound | null,
  key: string,
  sessionIds: ReadonlyArray<string>,
): { readonly round: SessionTimesRound; readonly ask: ReadonlyArray<string> } {
  const asked = round !== null && round.key === key ? round.asked : new Set<string>();
  const ask = [...new Set(sessionIds)].filter((id) => !asked.has(id));
  return { round: ask.length === 0 && round?.key === key ? round : { key, asked: new Set([...asked, ...ask]) }, ask };
}

export function isQuietSession(node: RoomRailSessionNode, rule: RoomRailQuietRule): boolean {
  const { sessionId } = node.session;
  const desk = rule.activity.get(sessionId);
  const world = rule.worldActivity?.get(sessionId);
  const known = desk === undefined ? world : world === undefined ? desk : Math.max(desk, world);
  const at = known ?? rule.birthBound?.(node.session.bornSeq);
  return (
    at !== undefined &&
    at < rule.cutoff &&
    !node.addressed &&
    !rule.pinned.has(sessionId) &&
    !rule.live(sessionId) &&
    node.children.every((child) => isQuietSession(child, rule))
  );
}

/**
 * The rail split in two: what stays in view, and what folds under Earlier.
 * A room keeps its place in view when it has recent sessions or had none at
 * all (a room just made); it joins Earlier only through its quiet sessions.
 */
export function splitQuietRoomRailGroups(
  groups: RoomRailGroups,
  rule: RoomRailQuietRule | null,
): { readonly recent: RoomRailGroups; readonly quiet: RoomRailGroups } {
  if (rule === null) return { recent: groups, quiet: { ...groups, rooms: [], parked: [] } };
  const recentRooms = [];
  const quietRooms = [];
  for (const entry of groups.rooms) {
    const quiet = entry.sessions.filter((node) => isQuietSession(node, rule));
    const recent = entry.sessions.filter((node) => !quiet.includes(node));
    if (recent.length > 0 || entry.sessions.length === 0)
      recentRooms.push({ ...entry, sessions: recent });
    if (quiet.length > 0) quietRooms.push({ ...entry, sessions: quiet });
  }
  return {
    recent: { ...groups, rooms: recentRooms },
    quiet: { ...groups, rooms: quietRooms, parked: [] },
  };
}
